'use client'

/**
 * Client-side WAPI service (composition-root binding for the standalone wapi-client calls).
 *
 * Built **once** from `config.wapi` — `urlRoot` and the federated `credentials` are bound at
 * construction — and hung on `getClientEnv().services.wapi`. Each method takes the **dynamic
 * user token per call** (the token comes from the localStorage mirror at call time, never from
 * config). Methods return the `Result` of the underlying `wapi-client` function unchanged and
 * never reject (`services.md#services-return-result`).
 */
import type Result from 'true-myth/result'

import type { ClientWapiConfig } from '@/config/schema'

import {
    federatedLogin,
    getUserInfo,
    identifyRequest,
    logoutRequest,
    refreshRequest,
    type ClientWapiError,
    type FederatedSession,
    type UserInfo,
} from './wapi-client'

/**
 * Client WAPI service. `urlRoot` + federated `credentials` are bound at construction; every
 * method takes the dynamic user token per call. Mirrors how `services.datadog` is wired.
 */
export interface ClientWapiService {
    /** Exchange a federated provider credential (Google) for a WAPI session (`auth.json`). */
    exchangeFederated(
        provider: 'google',
        providerToken: string,
    ): Promise<Result<FederatedSession, ClientWapiError>>
    /** Refresh an expired cognito session token (`refresh.json`). */
    refresh(opts: {
        email: string
        token: string
        sessionId?: string
    }): Promise<Result<FederatedSession, ClientWapiError>>
    /** Fetch the logged-in user's profile for the navbar greeting (`userinfo.json`). */
    getUserInfo(token: string): Promise<Result<UserInfo, ClientWapiError>>
    /** Invalidate the session server-side (`logout.json`). */
    logout(token: string): Promise<Result<void, ClientWapiError>>
    /** POST a `user:action:identify` event (`events.json`). */
    identify(token: string, data: Record<string, unknown>): Promise<Result<void, ClientWapiError>>
}

/**
 * Creates the client WAPI service from the client-safe `wapi` config. Pure + synchronous —
 * binds `urlRoot` + `credentials` and delegates to the existing `wapi-client` functions.
 */
export function createClientWapiService(config: ClientWapiConfig): ClientWapiService {
    const { urlRoot, credentials } = config

    return {
        exchangeFederated: (provider, providerToken) =>
            federatedLogin({ urlRoot, credentials, provider, token: providerToken }),
        refresh: (opts) => refreshRequest({ urlRoot, ...opts }),
        getUserInfo: (token) => getUserInfo({ urlRoot, token }),
        logout: (token) => logoutRequest(urlRoot, token),
        identify: (token, data) => identifyRequest({ urlRoot, token, data }),
    }
}
